/**
 * Drives the shared visibility timer for AtBatResultBanner and AtBatResultArrows.
 * Both consume the same atBatIndex + replayKey so they fade in lock-step.
 *
 * Visibility rules:
 *  - When `dismissedAtBatIndex === atBatIndex`, return visible=false immediately
 *    (user manually closed this at-bat's result).
 *  - Otherwise start an 8s timer from mount; resets on atBatIndex change OR
 *    replayKey bump.
 *  - When the timer fires, visible flips false and stays false until either
 *    atBatIndex changes (navigating Prev/Next) or replayKey bumps.
 */
import { useEffect, useState } from 'react';

const VISIBLE_MS = 8000;

interface Args {
  atBatIndex: number | null;
  dismissedAtBatIndex: number | null;
  replayKey: number;
}

export function useAtBatResultLifecycle({
  atBatIndex,
  dismissedAtBatIndex,
  replayKey,
}: Args): { visible: boolean } {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (atBatIndex == null) {
      setVisible(false);
      return undefined;
    }
    if (dismissedAtBatIndex === atBatIndex) {
      setVisible(false);
      return undefined;
    }
    setVisible(true);
    const t = window.setTimeout(() => setVisible(false), VISIBLE_MS);
    return () => window.clearTimeout(t);
  }, [atBatIndex, dismissedAtBatIndex, replayKey]);

  return { visible };
}
